<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useChatStore } from '../stores/chatStore';

const chatStore = useChatStore();
const { t } = useI18n();

const followUpQuestion = ref('');
const isTouched = ref(false);

const deepResearch = computed(() => chatStore.deepResearch);
const metadataEntries = computed(() => Object.entries(deepResearch.value.metadata ?? {}));
const hasMetadata = computed(() => metadataEntries.value.length > 0);
const isSubmitDisabled = computed(() => followUpQuestion.value.trim().length === 0);

const submitFollowUp = (event: Event) => {
  event.preventDefault();
  isTouched.value = true;
  if (isSubmitDisabled.value) {
    return;
  }
  chatStore.triggerFollowUp(followUpQuestion.value);
  followUpQuestion.value = '';
  isTouched.value = false;
};

const selectFollowUp = (question: string) => {
  followUpQuestion.value = '';
  chatStore.triggerFollowUp(question);
  isTouched.value = false;
};

const closePanel = () => {
  chatStore.toggleDeepResearch(false);
};

const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    case 'in_progress':
      return 'bg-sky-100 text-sky-700 border-sky-300';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-300';
  }
};

watch(
  () => chatStore.deepResearch.isPanelOpen,
  (isOpen) => {
    if (isOpen) {
      isTouched.value = false;
    }
  }
);
</script>

<template>
  <Transition name="fade-slide">
    <section v-if="deepResearch.isPanelOpen" class="deep-research-panel">
      <header class="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
        <div>
          <p class="text-xs uppercase tracking-wide text-gray-500">{{ t('deepResearch.sectionLabel') }}</p>
          <h2 class="text-xl font-semibold text-gray-900">{{ t('deepResearch.title') }}</h2>
        </div>
        <button type="button" class="rounded-full border border-gray-200 bg-white p-1 text-gray-500 transition hover:text-gray-700"
          :aria-label="t('deepResearch.closePanel')" @click="closePanel">
          <Icon name="material-symbols:close-rounded" size="24" />
        </button>
      </header>

      <div class="flex-1 overflow-y-auto px-6 pb-6 pt-4 space-y-8">
        <section>
          <h3 class="section-heading">{{ t('deepResearch.keywords') }}</h3>
          <div v-if="deepResearch.keywords.length" class="flex flex-wrap gap-2">
            <span v-for="keyword in deepResearch.keywords" :key="keyword"
              class="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-700">
              {{ keyword }}
            </span>
          </div>
          <p v-else class="section-empty">{{ t('deepResearch.noKeywords') }}</p>
        </section>

        <section>
          <h3 class="section-heading">{{ t('deepResearch.progress') }}</h3>
          <ul v-if="deepResearch.progressLog.length" class="timeline space-y-4">
            <li v-for="(log, index) in deepResearch.progressLog" :key="index" class="timeline-item">
              <div class="timeline-point" aria-hidden="true"></div>
              <p class="timeline-content">{{ log }}</p>
            </li>
          </ul>
          <p v-else class="section-empty">{{ t('deepResearch.noProgress') }}</p>
        </section>

        <section>
          <h3 class="section-heading">{{ t('deepResearch.steps') }}</h3>
          <ul v-if="deepResearch.steps.length" class="space-y-4">
            <li v-for="step in deepResearch.steps" :key="step.id" class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <h4 class="font-medium text-gray-900">{{ step.title }}</h4>
                <span class="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold"
                  :class="statusBadgeClass(step.status)">
                  <span class="inline-block h-2 w-2 rounded-full"
                    :class="{ 'bg-emerald-500': step.status === 'completed', 'bg-sky-500': step.status === 'in_progress', 'bg-gray-400': step.status === 'pending' }"></span>
                  {{ t(`deepResearch.stepStatus.${step.status}`) }}
                </span>
              </div>
              <p v-if="step.detail" class="mt-2 text-sm text-gray-600">{{ step.detail }}</p>
              <p v-if="step.timestamp" class="mt-1 text-xs text-gray-400">{{ step.timestamp }}</p>
            </li>
          </ul>
          <p v-else class="section-empty">{{ t('deepResearch.noSteps') }}</p>
        </section>

        <section>
          <h3 class="section-heading">{{ t('deepResearch.references') }}</h3>
          <ul v-if="deepResearch.references.length" class="space-y-4">
            <li v-for="reference in deepResearch.references" :key="reference.id"
              class="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <a :href="reference.url" target="_blank" rel="noopener"
                class="text-base font-medium text-primary-700 hover:underline">{{ reference.title }}</a>
              <p v-if="reference.snippet" class="mt-2 text-sm text-gray-600">{{ reference.snippet }}</p>
            </li>
          </ul>
          <p v-else class="section-empty">{{ t('deepResearch.noReferences') }}</p>
        </section>

        <section>
          <h3 class="section-heading">{{ t('deepResearch.summary') }}</h3>
          <p v-if="deepResearch.summary" class="rounded-lg border border-slate-200 bg-white p-4 text-sm text-gray-700">
            {{ deepResearch.summary }}
          </p>
          <p v-else class="section-empty">{{ t('deepResearch.noSummary') }}</p>
        </section>

        <section>
          <h3 class="section-heading">{{ t('deepResearch.metadata') }}</h3>
          <div v-if="hasMetadata" class="overflow-hidden rounded-lg border border-slate-200">
            <dl>
              <div v-for="[key, value] in metadataEntries" :key="key"
                class="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] border-b border-slate-100 bg-white px-4 py-3 last:border-b-0">
                <dt class="text-xs font-semibold uppercase tracking-wide text-slate-500">{{ key }}</dt>
                <dd class="text-sm text-slate-700">{{ value }}</dd>
              </div>
            </dl>
          </div>
          <p v-else class="section-empty">{{ t('deepResearch.noMetadata') }}</p>
        </section>

        <section>
          <div class="flex items-center justify-between">
            <h3 class="section-heading">{{ t('deepResearch.followUpTitle') }}</h3>
            <p class="text-xs text-gray-400">{{ t('deepResearch.followUpHelper') }}</p>
          </div>

          <div v-if="deepResearch.followUpQuestions.length" class="mt-3 flex flex-wrap gap-2">
            <button v-for="question in deepResearch.followUpQuestions" :key="question" type="button"
              class="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-primary-700 transition hover:border-primary-500 hover:bg-primary-50"
              @click="selectFollowUp(question)">
              {{ question }}
            </button>
          </div>

          <form class="mt-4 space-y-3" @submit="submitFollowUp">
            <label for="deep-research-followup" class="sr-only">{{ t('deepResearch.followUpPlaceholder') }}</label>
            <input id="deep-research-followup" v-model="followUpQuestion"
              class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              type="text" :placeholder="t('deepResearch.followUpPlaceholder')" @input="isTouched = false" />
            <p v-if="isTouched && isSubmitDisabled" class="text-xs text-red-600">{{ t('deepResearch.followUpRequired') }}</p>
            <div class="flex justify-end">
              <button type="submit" class="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-primary-300"
                :disabled="isSubmitDisabled">
                <Icon name="material-symbols:send-rounded" size="18" />
                {{ t('deepResearch.followUpSubmit') }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </section>
  </Transition>
</template>

<style scoped>
@reference "../assets/css/tailwind.css";

.deep-research-panel {
  @apply absolute inset-0 z-50 flex h-full w-full flex-col bg-white shadow-2xl sm:left-auto sm:w-[420px] sm:border-l sm:border-gray-200;
}

.section-heading {
  @apply text-sm font-semibold uppercase tracking-wide text-gray-500;
}

.section-empty {
  @apply rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-400;
}

.timeline {
  @apply relative pl-2;
}

.timeline::before {
  content: '';
  @apply absolute left-[10px] top-1 h-full border-l border-slate-200;
}

.timeline-item {
  @apply relative pl-6;
}

.timeline-point {
  @apply absolute left-[5px] top-1 h-3 w-3 rounded-full border-2 border-white shadow;
  background-color: #194593;
}

.timeline-content {
  @apply text-sm text-gray-700;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

.section-heading + p {
  @apply mt-2;
}

:global(.text-primary-700) {
  color: #194593;
}

:global(.hover\:bg-primary-50:hover) {
  background-color: #EFF4FF;
}

:global(.hover\:border-primary-500:hover) {
  border-color: #194593;
}

:global(.bg-primary-600) {
  background-color: #194593;
}

:global(.bg-primary-500) {
  background-color: #2a4c99;
}

:global(.bg-primary-700) {
  background-color: #0f2c63;
}

:global(.bg-primary-300) {
  background-color: #94b0df;
}

:global(.focus\:ring-primary-200:focus) {
  box-shadow: 0 0 0 3px rgba(25, 69, 147, 0.15);
}
</style>
